import {
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { ChevronIcon, CloseIcon } from "vega-icons";

import { DropdownOption } from "@vega-core/components/Form/Controls/Dropdown/Dropdown/DropdownOption";
import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { Key } from "@vega-core/constants/global";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { useDropdownState } from "./hooks/useDropdownState";

type Props = {
  name: string;
  value?: string[] | string;
  options?: OptionType[];
  onChange?: (value: OptionType) => void;
  label?: string | null;
  labelTag?: ReactNode;
  placeholder?: string | null;
  background?: string;
  compact?: boolean;
  withBorders?: boolean;
  showDescription?: boolean;
  isSearchable?: boolean;
  withCheckmark?: boolean;
  disabled?: boolean;
  isOptional?: boolean;
  error?: string;
  hint?: string;
  ariaLabel?: string;
  emptyListPlaceholder?: ReactElement;
  maxHeight?: string;
  fullWidth?: boolean;
  constrainInputWidth?: boolean;
};

/**
 * @deprecated This component is deprecated. Please use ThemedDropdown or SearchableDropdown instead.
 */
export function Dropdown({
  name,
  value,
  options = [],
  onChange,
  label,
  labelTag,
  placeholder,
  background,
  compact = false,
  withBorders = true,
  showDescription = true,
  isSearchable,
  withCheckmark,
  disabled,
  isOptional,
  error,
  hint,
  ariaLabel,
  emptyListPlaceholder,
  maxHeight = "max-h-40",
  fullWidth,
  constrainInputWidth = false,
}: Props) {
  const {
    focusedElement,
    setFocusedElement,
    showMenu,
    selectedValue,
    searchValue,
    setSearchValue,
    searchRef,
    inputRef,
    handleInputClick,
    onValueChange,
  } = useDropdownState({
    value,
    options,
    onChange,
  });

  const getDisplay = () => {
    if (
      (Array.isArray(selectedValue) && selectedValue.length === 0) ||
      (!Array.isArray(selectedValue) &&
        !selectedValue.value &&
        !selectedValue.label)
    ) {
      return null;
    }

    if (Array.isArray(selectedValue)) {
      return (
        <div className="flex flex-wrap gap-1">
          {selectedValue.map((option) => (
            <Tag
              dataTestId={`${name}-dropdown-tag-${option.value}`}
              key={option.value}
              variant={TagVariants.Default}
            >
              {option.label}
              <CloseIcon
                className="h-3 w-3 cursor-pointer"
                onClick={(e) => onTagRemove(e, option)}
              />
            </Tag>
          ))}
        </div>
      );
    }

    if (showDescription && isTruthy(selectedValue.description)) {
      return (
        <>
          <div className="body-base-regular text-text/primary">
            {selectedValue.label}
          </div>
          <div className="body-base-regular text-text/secondary">
            {selectedValue.description}
          </div>
        </>
      );
    }

    return (
      <div className="body-base-regular">
        {isTruthy(selectedValue.inputLabel)
          ? selectedValue.inputLabel
          : selectedValue.label}
      </div>
    );
  };

  const onTagRemove = (e: MouseEvent<Element>, option: OptionType) => {
    e.stopPropagation();
    onValueChange(option);
  };

  const onItemClick = (option: OptionType) => {
    onValueChange(option);
  };

  const isSelected = (option: OptionType) => {
    if (Array.isArray(selectedValue)) {
      return selectedValue.some((selected) => selected.value === option.value);
    }

    return selectedValue.value === option.value;
  };

  const onSearch = (e: ChangeEvent<HTMLInputElement>) =>
    setSearchValue(e.target.value);

  const getOptions = () => {
    if (!searchValue || !showMenu) {
      return options;
    }

    const searchValueLower = searchValue.toLowerCase();

    return options
      .filter((option) => option.label.toLowerCase().includes(searchValueLower))
      .sort((a, b) => {
        const aLabel = a.label.toLowerCase();
        const bLabel = b.label.toLowerCase();

        if (
          aLabel.startsWith(searchValueLower) &&
          !bLabel.startsWith(searchValueLower)
        ) {
          return -1;
        }

        if (
          !aLabel.startsWith(searchValueLower) &&
          bLabel.startsWith(searchValueLower)
        ) {
          return 1;
        }

        if (
          aLabel.includes(searchValueLower) &&
          !bLabel.includes(searchValueLower)
        ) {
          return -1;
        }

        if (
          !aLabel.includes(searchValueLower) &&
          bLabel.includes(searchValueLower)
        ) {
          return 1;
        }

        return 0;
      });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;

    if (key === "ArrowRight" || key === "ArrowDown") {
      e.preventDefault();
      focusToNextInput();
    }

    if (key === "ArrowLeft" || key === "ArrowUp") {
      e.preventDefault();
      focusToPrevInput();
    }

    if (key === Key.Enter && isTruthy(focusedElement)) {
      document.getElementById(`item-${focusedElement}`)?.click();
    }
  };

  const focusToNextInput = () => {
    const optionsLength = getOptions().length - 1;

    const newElement = focusedElement !== undefined ? focusedElement + 1 : 0;
    const nextElement = newElement > optionsLength ? 0 : newElement;

    setFocusedElement(nextElement);
    document.getElementById(`item-${nextElement}`)?.focus();
  };

  const focusToPrevInput = () => {
    const optionsLength = getOptions().length - 1;
    const newElement =
      isFalsy(focusedElement) || focusedElement === 0
        ? optionsLength
        : focusedElement - 1;

    setFocusedElement(newElement);
    document.getElementById(`item-${newElement}`)?.focus();
  };

  const isEmptyListPlaceholderVisible = options.length === 0;
  const isNoSearchResultsVisible =
    isTruthy(searchValue) && getOptions().length === 0;
  const isItemSelected = isTruthy(getDisplay());
  const filteredOptions = getOptions();

  return (
    <div className={cnTwMerge(fullWidth && "w-full")}>
      {isTruthy(label) ? (
        <div className={cnTwMerge(fullWidth && "w-full")}>
          <FormLabel
            className={cnTwMerge("cursor-pointer", {
              "opacity-50": disabled,
            })}
            compact={compact}
            hint={hint}
            isOptional={isOptional}
            label={label}
            tag={labelTag}
          />
        </div>
      ) : null}
      <div>
        <div
          className={cnTwMerge(
            "defaultTransition active:bg-button/secondary/background/pressed relative rounded-companyBorderRadius border border-transparent",
            compact ? "min-h-8 box-border" : "min-h-11",
            constrainInputWidth && "max-w-[50%]",
            background,
            {
              "cursor-pointer hover:border-brand-default focus-within:border-brand-default":
                !disabled,
              "border border-border/primary": isTruthy(withBorders),
              "border border-border/negative": isTruthy(error),
            },
          )}
          data-testid={`${name}-dropdown-container`}
        >
          <div
            aria-disabled={disabled}
            aria-label={cnTwMerge(
              "Dropdown",
              isTruthy(ariaLabel) ? ariaLabel : label,
            )}
            className={cnTwMerge(
              "flex text-text/primary select-none items-center justify-between rounded-companyBorderRadius transition hover:border-brand-default bg-background/primary px-2",
              compact ? "py-0 h-[30px]" : "py-[7px]",
              {
                "!border-border/negative": isTruthy(error),
                "bg-background/secondary": disabled,
              },
            )}
            data-testid={`${name}-dropdown`}
            onClick={() => !disabled && handleInputClick()}
            ref={inputRef}
          >
            <div className="flex items-center w-full min-w-0">
              {!Array.isArray(selectedValue) && isTruthy(selectedValue.icon) ? (
                <div className="flex-shrink-0 mr-2">
                  {typeof selectedValue.icon === "string" ? (
                    <img className="h-6 w-6" src={selectedValue.icon} />
                  ) : (
                    selectedValue.icon
                  )}
                </div>
              ) : null}
              <div
                className={cnTwMerge("table h-7 w-full table-fixed", {
                  "text-text/primary": isItemSelected,
                })}
              >
                <div
                  className={cnTwMerge(
                    "dropdown-selected-value table-cell overflow-hidden text-ellipsis whitespace-nowrap pr-1 align-middle body-base-medium text-base",
                    {
                      "text-text/disabled": disabled,
                    },
                  )}
                >
                  <div
                    className={cnTwMerge({
                      "text-text/disabled": disabled,
                    })}
                  >
                    {isItemSelected ? (
                      getDisplay()
                    ) : (
                      <span className="text-text/disabled body-base-regular">
                        {placeholder}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1" tabIndex={0}>
              <ChevronIcon
                className={cnTwMerge(
                  "h-4 w-4 shrink-0 text-text/secondary group-hover:text-text/interactive defaultTransition",
                  { "rotate-180": showMenu, "opacity-50": disabled },
                )}
              />
            </div>
          </div>
          {showMenu ? (
            <div
              className={cnTwMerge(
                "drop-down-box-shadow-light absolute z-50 w-full min-w-56 translate-y-1 overflow-auto rounded-companyBorderRadius border-none bg-dark-gray",
                !isEmptyListPlaceholderVisible && !isNoSearchResultsVisible
                  ? maxHeight
                  : "",
              )}
              onKeyDown={handleKeyDown}
              role="listbox"
            >
              {isTruthy(isSearchable) && options.length > 0 ? (
                <div className="p-1 bg-dark-gray">
                  <input
                    className="box-border w-full p-1 border rounded-companyBorderRadius border-brand-default focus:border-brand-default bg-dark-gray focus:ring-0 focus:ring-offset-0"
                    data-testid={`${name}-dropdown-search`}
                    onChange={onSearch}
                    ref={searchRef}
                    tabIndex={0}
                    value={searchValue}
                  />
                </div>
              ) : null}
              {filteredOptions.map((option, index) => (
                <DropdownOption
                  index={index}
                  isDisabled={option.disabled}
                  isSelected={isSelected}
                  key={option.value}
                  name={name}
                  onItemClick={onItemClick}
                  option={option}
                  withCheckmark={withCheckmark}
                />
              ))}
              {isEmptyListPlaceholderVisible ? emptyListPlaceholder : null}
              {isNoSearchResultsVisible ? (
                <div className="p-2 text-text/secondary body-base-regular">
                  {m.formFields_dropdown_noOptions()}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        {isTruthy(error) ? (
          <ValidationErrorText dataTestId={error} withMargin>
            {error}
          </ValidationErrorText>
        ) : null}
      </div>
    </div>
  );
}
